/**
 * 操作按钮组件（固定在弹窗底部）
 */
import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Space } from 'antd';
import type { MenuProps } from 'antd';
import {
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  BarChartOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { ReportService } from '../../services/reportService';

interface ActionButtonsProps {
  project: ProjectOverviewData;
  onEditProject?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  project,
  onEditProject,
  isMobile = false,
}) => {
  const [exporting, setExporting] = useState(false);

  const handleEditProject = () => {
    onEditProject?.(project);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await ReportService.exportAndDownloadExcel(project.id, project.name);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await ReportService.exportAndDownloadPDF(project.id, project.name);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenFullAnalysis = () => {
    window.location.href = `/reports/project-comprehensive?projectId=${project.id}`;
  };

  const handleOpenDashboard = () => {
    window.location.href = `/dashboard?projectId=${project.id}`;
  };

  const handleOpenWorkTimeList = () => {
    window.location.href = `/worktime?projectId=${project.id}`;
  };

  const handleOpenMaterialList = () => {
    window.location.href = `/material?projectId=${project.id}`;
  };

  const handleOpenEquipmentList = () => {
    window.location.href = `/equipment?projectId=${project.id}`;
  };

  const moreMenuItems = useMemo<MenuProps['items']>(() => [
    { key: 'worktime-list', label: '打开完整记录页 · 工时', icon: <UnorderedListOutlined />, onClick: handleOpenWorkTimeList },
    { key: 'material-list', label: '打开完整记录页 · 材料', icon: <UnorderedListOutlined />, onClick: handleOpenMaterialList },
    { key: 'equipment-list', label: '打开完整记录页 · 设备', icon: <UnorderedListOutlined />, onClick: handleOpenEquipmentList },
    { type: 'divider' },
    { key: 'export-excel', label: '导出 Excel 报表', icon: <FileExcelOutlined />, onClick: handleExportExcel },
    { key: 'export-pdf', label: '导出 PDF 报表', icon: <FilePdfOutlined />, onClick: handleExportPDF },
    { type: 'divider' },
    { key: 'edit-project', label: '编辑项目', icon: <EditOutlined />, onClick: handleEditProject },
    { key: 'dashboard', label: '打开驾驶舱', icon: <ProfileOutlined />, onClick: handleOpenDashboard },
  ], []);

  const wrapperStyle: React.CSSProperties = {
    padding: isMobile ? '12px 0' : '16px 0',
    borderTop: '1px solid rgba(16, 27, 35, 0.08)',
  };

  if (isMobile) {
    return (
      <div style={{ ...wrapperStyle, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button icon={<BarChartOutlined />} onClick={handleOpenFullAnalysis} block>
          查看完整经营分析
        </Button>

        <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="topCenter">
          <Button icon={<MoreOutlined />} loading={exporting} block>
            更多操作 <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button icon={<BarChartOutlined />} onClick={handleOpenFullAnalysis}>
          查看完整经营分析
        </Button>

        <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="topRight">
          <Button icon={<MoreOutlined />} loading={exporting}>
            更多操作 <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

export default ActionButtons;
